import {createAction} from 'redux-actions'
import * as ActionTypes from './actionTypes'
import {ICalTabType, NO_RIGHT_FOR_RPC_EXCEPTION, NO_SUCH_ICAL_FEED_SETTINGS_EXCEPTION,} from './constants'
import {Exceptions as ICalendarExceptions} from '../../../js/common/icalendar/constants'
import {saveICalInfoToLocalStorage} from './utils'
import {getActiveTab, getAllFeedsMap, getICalInfo} from './selectors'
import {copyToClipboard} from '../../../js/atBrowserUtils'
import {canManageAccountsAndPermissions} from '../../../js/loggedInUser/selectors'
import messageResource from '../../../js/messageResource'
import {toast} from '../../../js/toasts/actions'
import RpcError from '../../../js/util/rpc/RpcError'
import {rpcCall} from '../../../js/util/rpc/rpcCalls'
import {setLeaveTypesList, setTimeZoneGroupsList, setUserGroupsList, setUsersList} from '../../../js/entities/actions'
import {batched} from '../../../js/redux/batch'
import {createActionObject} from '../../../js/redux/utils'

export const setActiveTab = createAction(ActionTypes.ACTIVE_TAB_SET)
export const setLoading = createAction(ActionTypes.LOADING_SET)
export const setSharedFeeds = createAction(ActionTypes.SHARED_FEEDS_LIST_SET)
export const setAllFeeds = createAction(ActionTypes.ALL_FEEDS_LIST_SET)
export const setAllSorting = createAction(ActionTypes.ALL_SORTING_SET)
export const setSharedSorting = createAction(ActionTypes.SHARED_SORTING_SET)
export const setRecentlyConfiguredFeedId = createAction(ActionTypes.RECENTLY_CONFIGURED_FEED_ID_SET)
export const setFeedsContentLoading = createAction(ActionTypes.FEEDS_CONTENT_LOADING_SET)


export const changeAllSorting = sorting => (dispatch, getState) => {
    dispatch(setAllSorting(sorting))
    saveICalInfoToLocalStorage(getICalInfo(getState()))
}

export const changeSharedSorting = sorting => (dispatch, getState) => {
    dispatch(setSharedSorting(sorting))
    saveICalInfoToLocalStorage(getICalInfo(getState()))
}

export const changeActiveTab = activeTab => (dispatch, getState) => {
    dispatch(setFeedsContentLoading(true))
    dispatch(setActiveTab(activeTab))
    dispatch(loadActualFeeds)
    saveICalInfoToLocalStorage(getICalInfo(getState()))
    dispatch(setFeedsContentLoading(false))
}

export const regenerateFeedUrl = (id, successCallback) => (dispatch, getState) => {
    dispatch(createActionObject(ActionTypes.RENGERATE_FEED_LINK_DATA_LOAD))
    rpcCall('ICalFeedSettingsService.regenerateFeedLinks', [id], handleExceptions)
        .then(() => {
            successCallback()
            dispatch(toast(messageResource.getMessage('icalendar.dialog.regenerate.success')))
            const currentFeed = getAllFeedsMap(getState())[id]
            dispatch(createActionObject(ActionTypes.RENGERATE_FEED_LINK_DATA_LOAD_SUCCESS, {
                ...currentFeed,
                lastSyncDate: null,
            }))
        })
        .catch(error => {
            dispatch(createActionObject(ActionTypes.RENGERATE_FEED_LINK_DATA_LOAD_FAILURE))
            dispatch(handleError(error))
        })
}

export const deleteFeed = (id, successCallback) => dispatch => {
    dispatch(createActionObject(ActionTypes.DELETE_FEED_DATA_LOAD))
    rpcCall('ICalFeedSettingsService.deleteICalFeedSettings', [id], handleExceptions)
        .then(() => {
            successCallback()
            dispatch(createActionObject(ActionTypes.DELETE_FEED_DATA_LOAD_SUCCESS, id))
            dispatch(toast(messageResource.getMessage('icalendar.dialog.delete.success')))
        })
        .catch(error => {
            dispatch(createActionObject(ActionTypes.DELETE_FEED_DATA_LOAD_FAILURE))
            dispatch(handleError(error))
        })
}

export const startPageLoading = () => (dispatch, getState) => {
    dispatch(setLoading(true))
    const state = getState()
    if (!canManageAccountsAndPermissions(state)) {
        dispatch(setActiveTab(ICalTabType.USER_FEEDS))
    } else if (!getActiveTab(state)) {
        dispatch(setActiveTab(ICalTabType.ALL_FEEDS))
    }
    dispatch(loadActualFeeds)
}

const loadActualFeeds = (dispatch, getState) => {
    if (getActiveTab(getState()) === ICalTabType.ALL_FEEDS) {
        dispatch(loadAllFeeds)
    } else {
        dispatch(loadSharedFeeds)
    }
}

const setFeedSettingsData = (result, setActualFeeds) => batched((dispatch) => {
    dispatch(setEntitiesByICalResult(result.entitiesInfo))
    dispatch(setActualFeeds(result.feedSettings))
})

export const setEntitiesByICalResult = ({
    leaveTypes = [],
    users = [],
    userGroups = [],
    timeZoneGroups = [],
}) => batched((dispatch) => {
    dispatch(setLeaveTypesList(leaveTypes))
    dispatch(setUsersList(users))
    dispatch(setUserGroupsList(userGroups))
    dispatch(setTimeZoneGroupsList(timeZoneGroups))
})

const loadSharedFeeds = (dispatch) => {
    rpcCall('ICalFeedSettingsService.getSharedWithMeFeedSettingsInfo', [], handleExceptions)
        .then(result => {
            dispatch(setFeedSettingsData(result, setSharedFeeds))
            dispatch(setLoading(false))
        })
        .catch(error => {
            dispatch(setLoading(false))
            dispatch(handleError(error))
        })
}

const loadAllFeeds = (dispatch) => {
    rpcCall('ICalFeedSettingsService.getAllFeedSettingsInfo', [], handleExceptions)

        .then(result => {
            dispatch(setFeedSettingsData(result, setAllFeeds))
            dispatch(setLoading(false))
        })
        .catch(error => {
            dispatch(setLoading(false))
            dispatch(handleError(error))
        })
}

export const getFeedUrl = (id, onSuccessCb = (url) => {}, onFailureCb = () => {}) => dispatch => {
    rpcCall('ICalFeedSettingsService.getFeedAccessURL', [id], handleExceptions)
        .then(result => {
            onSuccessCb(result)
        })
        .catch(error => {
            onFailureCb()
            dispatch(handleError(error))
        })
}

export const copyLink = (link) => dispatch => {
    copyToClipboard(link)
    dispatch(toast(messageResource.getMessage('icalendar.dialog.copy.success')))
}

export const handleExceptions = rpcError => {
    rpcError.handleOnException(NO_RIGHT_FOR_RPC_EXCEPTION)
    rpcError.handleOnException(NO_SUCH_ICAL_FEED_SETTINGS_EXCEPTION)
    rpcError.handleOnException(ICalendarExceptions.EMPTY_COMPONENTS_EXCEPTION)
}

export const handleError = (error) => dispatch => {
    if (error instanceof RpcError) {
        switch (error.getException()) {
            case NO_RIGHT_FOR_RPC_EXCEPTION:
                alert(messageResource.getMessage('common.concurrent.exception.alert'))
                window.location.reload()
                return
            case NO_SUCH_ICAL_FEED_SETTINGS_EXCEPTION:
                alert(messageResource.getMessage('common.concurrent.exception.alert'))
                window.location.reload()
                return
            case ICalendarExceptions.EMPTY_COMPONENTS_EXCEPTION:
                alert(messageResource.getMessage('icalendar.pagee.lightbox.export.no_leaves'))
                return
        }
    }
    console.error(error)
}



// WEBPACK FOOTER //
// ./web/actiplans/js/icalendar/actions.js